import React from 'react';
import {
    Hooks,
    TableInstance,
    Row,
    MetaBase,
    ActionType,
    TableState,
    useGetLatest,
    useMountedLayoutEffect
} from 'react-table';

type OurActions = {
    toggleRowSplitView: 'toggleRowSplitView';
    resetSplitView: 'resetSplitView';
};
const actions: Record<string, string> & OurActions = {
    toggleRowSplitView: 'toggleRowSplitView',
    resetSplitView: 'resetSplitView'
};

const pluginName = 'useSplitView';

function useInstance<D extends object>(instance: TableInstance<D>) {
    const {
        dispatch,
        state: { splitViewRowId },
        autoResetSplitView = true,
        rows,
        data,
        manualSplitView,
        splitViewRowId: controlledSV
    } = instance;

    const toggleRowSplitView = React.useCallback(
        (id, value) => {
            dispatch({ type: actions.toggleRowSplitView, id, value });
        },
        [dispatch]
    );

    const selectedSplitRow = React.useMemo(() => {
        let retRow = null;
        rows.forEach((row) => {
            const valToUse = manualSplitView ? controlledSV : splitViewRowId;
            const isSplit = row.id === valToUse;

            row.isSplit = !!isSplit;

            if (isSplit) {
                retRow = row;
            }
        });

        return retRow;
    }, [rows, splitViewRowId, manualSplitView, controlledSV]);

    const resetSplitView = React.useCallback(() => {
        dispatch({ type: actions.resetSplitView });
    }, [dispatch]);

    const getAutoResetSplitView = useGetLatest(autoResetSplitView);
    useMountedLayoutEffect(() => {
        if (getAutoResetSplitView()) {
            dispatch({ type: actions.resetSplitView });
        }
    }, [dispatch, data]);

    Object.assign(instance, {
        toggleRowSplitView,
        selectedSplitRow,
        resetSplitView
    });
}

function prepareRow(row: Row<any>, { instance }: MetaBase<any>) {
    row.toggleSplitView = (set) => instance.toggleRowSplitView(row.id, set);
}

function reducer(
    state: TableState<any>,
    action: ActionType
    // _previousState?: TableState<any>,
    // instance?: TableInstance<any>
) {
    if (action.type === actions.init) {
        return {
            ...state,
            splitViewRowId: null
        };
    }
    if (action.type === actions.toggleRowSplitView) {
        const { id, value } = action;
        const currentId = state.splitViewRowId;
        let splitViewRowId = null;
        if (value !== undefined) {
            if (value) splitViewRowId = id;
        } else if (currentId !== id) {
            splitViewRowId = id;
        }
        return {
            ...state,
            splitViewRowId
        };
    }
    if (action.type === actions.resetSplitView) {
        return {
            ...state,
            splitViewRowId: null
        };
    }
    return state;
}

export const useSplitView = <D extends object = {}>(hooks: Hooks<D>) => {
    hooks.stateReducers.push(reducer);
    hooks.useInstance.push(useInstance);
    hooks.prepareRow.push(prepareRow);
};

useSplitView.pluginName = pluginName;
